import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { DateTime } from 'luxon';

import { url } from '../helpers/url';

import { PropakWeek } from '../models/propak-week';
import { SharedSettings } from '../models/shared-settings';
import { CalendarDefinition, CalendarDefinitionDetail } from '../models/calendar-definition';

@Injectable({
    providedIn: 'root'
})
export class CalendarService {

    constructor(private http: HttpClient, private settings: SharedSettings) { }

    GetCalendarDefinitions(): Observable<CalendarDefinitionDetail[]> {
        return this.http.get<CalendarDefinitionDetail[]>(this.settings.apiBaseUrl + url`calendar`);
    }

    GetCalendarDefinition(definition: CalendarDefinition): Observable<CalendarDefinitionDetail> {
        return this.http.get<CalendarDefinitionDetail>(this.settings.apiBaseUrl + url`calendar/${definition}`);
    }

    GetWeeksForYear(year: number, calendarDefinition?: CalendarDefinition): Observable<PropakWeek[]> {
        const cal = calendarDefinition ?? CalendarDefinition.Propak;
        return this.http.get<PropakWeek[]>(this.settings.apiBaseUrl + url`calendar/${cal}/${year}`)
            .pipe(map(weeks => weeks.map(w => this.ParseDates(w))));
    }

    GetWeeksForMonth(year: number, month: number, calendarDefinition?: CalendarDefinition): Observable<PropakWeek[]> {
        const cal = calendarDefinition ?? CalendarDefinition.Propak;
        return this.http.get<PropakWeek[]>(this.settings.apiBaseUrl + url`calendar/${cal}/${year}/${month}`)
            .pipe(map(weeks => weeks.map(w => this.ParseDates(w))));
    }

    GetForDate(date: DateTime): Observable<PropakWeek> {
        return this.http.get<PropakWeek>(this.settings.apiBaseUrl + url`calendar/week?date=${date.toISODate()}`)
            .pipe(map(week => this.ParseDates(week)));
    }

    GetMonthRangesForYear(year: number, calendarDefinition?: CalendarDefinition): Observable<{ nominalMonth: number; startDate: DateTime; endDate: DateTime }[]> {
        const cal = calendarDefinition ?? CalendarDefinition.Propak;
        return this.http.get<{ nominalMonth: number; startDate: string; endDate: string }[]>(
                this.settings.apiBaseUrl + url`calendar/monthranges/${cal}/${year}`)
            .pipe(map(ranges => ranges.map(r => ({
                nominalMonth: r.nominalMonth,
                startDate: DateTime.fromISO(r.startDate),
                endDate: DateTime.fromISO(r.endDate)
            }))));
    }

    GetWeeksAroundDate(date: DateTime, weeksBefore: number, weeksAfter: number): Observable<PropakWeek[]> {
        return this.http.get<PropakWeek[]>(
                this.settings.apiBaseUrl + url`calendar/arounddate/${date.toISODate()}?before=${weeksBefore}&after=${weeksAfter}`)
            .pipe(map(weeks => weeks.map(w => this.ParseDates(w))));
    }

    private ParseDates(week: PropakWeek): PropakWeek {
        week.weekStart = DateTime.fromISO(<any> week.weekStart);
        week.weekEnd = DateTime.fromISO(<any> week.weekEnd);
        return week;
    }
}
